// Browser mirror of the signature-spacing helpers in
// functions/_lib/communications-send.js (trimTrailingEmptyBlocks /
// trimLeadingEmptyBlocks). The server trims the empty edge between a message
// and its Gmail signature before sending; the composer's signature preview uses
// these so it shows the signature the way it will arrive — without the blank
// lines Gmail signatures often open with. Keep the two in step:
// functions/_lib/communications-send.signature.test.js runs both over one
// fixture list and asserts identical output.
//
// Only the empty edge is trimmed: whitespace, &nbsp;, <br>, and p/div/span
// elements holding nothing else. Text, images, links, rules and empty boxes that
// draw a border or background stop the trim; nothing in the middle is touched.

const EMPTY_BOX_TAGS = new Set(['p', 'div', 'span', 'o:p']);
const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
const DRAWS = /border|background|bgcolor/i;
const NBSP_AT_END = /(?:&nbsp;|&#160;|&#xa0;)$/i; // all six characters long
const BR_TAG = /^<br\b[^>]*>$/i;
const CLOSE_TAG = /^<\/([a-z][a-z0-9:-]*)\s*>$/i;
const OPEN_TAG = /^<([a-z][a-z0-9:-]*)\b([^>]*)>$/i;
const BLANK_AT_START = /^(?:&nbsp;|&#160;|&#xa0;|<br\b[^>]*>)/i;
const CLOSE_TAG_AT_START = /^<\/([a-z][a-z0-9:-]*)\s*>/i;
const OPEN_TAG_AT_START = /^<([a-z][a-z0-9:-]*)\b([^>]*)>/i;

export function trimTrailingEmptyBlocks(html) {
  let s = String(html ?? '');
  const closers = []; // closing tags stepped over, outermost first
  for (;;) {
    s = s.trimEnd();
    if (NBSP_AT_END.test(s.slice(-6))) { s = s.slice(0, -6); continue; }
    // The tag s ends with, if any: it starts at the first '<' after the
    // previous '>' — looked up directly so each step costs the tag, not the body.
    if (!s.endsWith('>')) break;
    const at = s.indexOf('<', s.lastIndexOf('>', s.length - 2) + 1);
    if (at < 0) break;
    const tag = s.slice(at);
    if (BR_TAG.test(tag)) { s = s.slice(0, at); continue; }
    let m = tag.match(CLOSE_TAG);
    if (m) { closers.push({ tag, name: m[1].toLowerCase() }); s = s.slice(0, at); continue; }
    m = tag.match(OPEN_TAG);
    const inner = closers[closers.length - 1];
    if (m && inner && inner.name === m[1].toLowerCase() && EMPTY_BOX_TAGS.has(inner.name) && !DRAWS.test(m[2])) {
      closers.pop(); s = s.slice(0, at); continue;
    }
    break;
  }
  return s + closers.reverse().map((c) => c.tag).join('');
}

export function trimLeadingEmptyBlocks(html) {
  let s = String(html ?? '');
  const openers = []; // opening tags stepped over, outermost first
  for (;;) {
    s = s.trimStart();
    let m = s.match(BLANK_AT_START);
    if (m) { s = s.slice(m[0].length); continue; }
    m = s.match(CLOSE_TAG_AT_START);
    if (m) {
      const inner = openers[openers.length - 1];
      if (inner && inner.name === m[1].toLowerCase() && EMPTY_BOX_TAGS.has(inner.name) && !DRAWS.test(inner.attrs)) {
        openers.pop(); s = s.slice(m[0].length); continue;
      }
      break;
    }
    m = s.match(OPEN_TAG_AT_START);
    if (m && !VOID_TAGS.has(m[1].toLowerCase()) && !/\/\s*$/.test(m[2])) {
      openers.push({ tag: m[0], name: m[1].toLowerCase(), attrs: m[2] }); s = s.slice(m[0].length); continue;
    }
    break;
  }
  return openers.map((o) => o.tag).join('') + s;
}
